import React from "react";
import CircleChart from "@appComponents/CircleChart/CircleChart";
import Big from "big.js";
import IMyEquityCompanies, {
  IEquityCompany,
} from "../MyEquityCompanies/IMyEquityCompanies";
import { TPortfolioChart } from "./IPortfolioChart";

const big = Big();

import * as style from "../HeldCompanies/HeldCompaniesStyle.module.scss";

export default class PortfolioChart extends React.Component<
  IMyEquityCompanies,
  TPortfolioChart
> {
  constructor(props: IMyEquityCompanies) {
    super(props);
    const chartData = this.getChartData(this.props.equityCompanies);

    this.state = {
      series: chartData.data,
      labels: chartData.labels,
      sum: chartData.sum,
      colors: ["#d027ff", "#ff3737", "#FA6400", "#37A1FF", "#F7B501"],
    };
  }

  componentDidMount() {
    const items = this.chartPieElements();
    if (!items) return;

    Array.from(items).forEach((element: HTMLElement) => {
      const pathElement = element.getElementsByTagName("path").item(0);
      if (!pathElement) return;
      pathElement.addEventListener("mouseenter", (e) =>
        this.toggleHighlightEquityClass(element)
      );
      pathElement.addEventListener("mouseleave", (e) =>
        this.toggleHighlightEquityClass(element)
      );

      element.addEventListener("chartAnimationOn", (e: CustomEvent) =>
        this.showSelectedCompany(element, e)
      );
      element.addEventListener("chartAnimationOff", (e: CustomEvent) =>
        this.hideSelectedCompany(element, e)
      );
    });
  }

  componentWillUnmount() {
    const items = this.chartPieElements();
    if (!items) return;

    Array.from(items).forEach((element: HTMLElement) => {
      const pathElement = element.getElementsByTagName("path").item(0);
      if (!pathElement) return;
      pathElement.removeEventListener("mouseenter", (e) =>
        this.toggleHighlightEquityClass(element)
      );
      pathElement.removeEventListener("mouseleave", (e) =>
        this.toggleHighlightEquityClass(element)
      );

      element.removeEventListener("chartAnimationOn", (e: CustomEvent) =>
        this.showSelectedCompany(element, e)
      );
      element.removeEventListener("chartAnimationOff", (e: CustomEvent) =>
        this.hideSelectedCompany(element, e)
      );
    });
  }

  chartPieElements = (): HTMLCollectionOf<Element> | undefined => {
    const wrapper = document.getElementById("circle-chart");
    if (!wrapper) return;
    return wrapper.getElementsByClassName("apexcharts-pie-series");
  };

  showSelectedCompany = (elem: HTMLElement, e: CustomEvent) => {
    const selectedChart = elem.children.item(0) as HTMLElement;
    selectedChart.classList.add(e.detail.activeClass);
    selectedChart.classList.add(e.detail.activeColor);
    selectedChart!.style.webkitTransition = "all 1s";
    this.forceUpdate();
  };

  hideSelectedCompany = (elem: HTMLElement, e: CustomEvent) => {
    const selectedChart = elem.children.item(0) as HTMLElement;
    selectedChart.classList.remove(e.detail.activeClass);
    selectedChart.classList.remove(e.detail.activeColor);
  };

  toggleHighlightEquityClass = (element: Element) => {
    const selectedCompany = element!.classList.value.split(" ").pop();
    Array.from(
      document.getElementsByClassName(
        `${selectedCompany!}_highlighted_by_chart`
      )
    ).forEach((elem: HTMLElement) => {
      const activeClassName = elem.getAttribute("data-color");
      if (!activeClassName) return;
      elem.classList.contains(activeClassName)
        ? elem.classList.remove(activeClassName!)
        : elem.classList.add(activeClassName!);
    });
  };

  getChartData(companies: IEquityCompany[]) {
    const companiesSortedByValue = [...companies].sort(
      (a, b) => Number(b.equity.value) - Number(a.equity.value)
    );
    let topCompanies: [number, string][] = companiesSortedByValue
      .slice(0, 4)
      .map((el) => [Number(el.equity.value), el.details.name]);
    const restCompanies = this.reduceValue(companies.slice(4));
    const sum = this.reduceValue(companies);

    if (restCompanies) {
      topCompanies = [...topCompanies, [restCompanies, "Other"]];
    }

    return {
      sum,
      data: topCompanies.map((el) => el[0]),
      labels: topCompanies.map((el) => el[1]),
    };
  }

  reduceValue(data: IEquityCompany[]) {
    return Number(
      data.reduce(
        (prev: Big, curr: IEquityCompany) => prev.add(curr.equity.value),
        big(0)
      )
    );
  }

  render() {
    return <CircleChart {...this.state} headline="Estimated portfolio value" />;
  }
}
